/**
 * SubcontractorSection
 * Consolidated component for the TG tab in AssignmentDetail
 * Single box containing: TG header with selector, and SubcontractorStatusCard for each TG
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../lib/auth'
import {
  Search,
  Leaf,
  Plus,
  Loader2,
  MessageSquare,
  Users,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { SubcontractorStatusCard } from './SubcontractorStatusCard'
import { AdminReportForm, type ReportedArticle, type ReportImage } from './AdminReportForm'
import { SendMultiSMSModal } from '../SendMultiSMSModal'
import { logAssignmentChange } from '../../services/activityLog'
import { lockPricesForAssignment } from '../../services/statistics'
import { sendSMS } from '../../services/sms'
import type { TgWorkStatus, TgInvoiceStatus } from '../../types/statusTypes'

interface Subcontractor {
  id: string
  name: string
  email?: string
  phone?: string
  color?: string
}

interface SubcontractorReport {
  id: string
  assignment_id: string
  subcontractor_id: string
  subcontractors: Subcontractor
  tg_status?: TgWorkStatus
  tg_invoice_status?: TgInvoiceStatus
  reported_at?: string
  attested_at?: string
  attested_by?: string
  report_text?: string
  reported_articles?: ReportedArticle[]
  report_images?: ReportImage[]
  is_draft?: boolean
  admin_feedback?: string
  feedback_at?: string
  declined_at?: string
  declined_reason?: string
  tg_invoice_received_at?: string
  tg_invoice_number?: string
  draft_saved_at?: string
  draft_saved_by?: string
}

interface PlannedArticle {
  articleId: string
  quantity: number
  subcontractorQuantities?: Record<string, number>
}

interface Article {
  id: string
  name: string
  unit?: string | null
}

interface SubcontractorSectionProps {
  assignmentId: string
  isNewAssignment: boolean
  isLocked: boolean
  assignmentStatus: string
  assignmentTitle: string
  assignmentNumber?: string
  assignmentDate?: string
  assignmentAddress?: string
  customerInvoiceStatus?: string
  selectedSubcontractorIds: string[]
  subcontractors: Subcontractor[]
  subcontractorReports: SubcontractorReport[]
  plannedArticles: PlannedArticle[]
  articles: Article[]
  onAddSubcontractor: (id: string) => void
  onRemoveSubcontractor: (id: string) => void
  onStatusChange: (status: string) => void
  refetchReports: () => void
  // Collapse props
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export function SubcontractorSection({
  assignmentId,
  isNewAssignment,
  isLocked,
  assignmentStatus,
  assignmentTitle,
  assignmentNumber,
  assignmentDate,
  assignmentAddress,
  customerInvoiceStatus,
  selectedSubcontractorIds,
  subcontractors,
  subcontractorReports,
  plannedArticles,
  articles,
  onAddSubcontractor,
  onRemoveSubcontractor,
  onStatusChange,
  refetchReports,
  isCollapsed = false,
  onToggleCollapse
}: SubcontractorSectionProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  // Local UI state
  const [showSubcontractorSelector, setShowSubcontractorSelector] = useState(false)
  const [subcontractorSearch, setSubcontractorSearch] = useState('')
  const subcontractorDropdownRef = useRef<HTMLDivElement>(null)

  // Admin reporting state
  const [adminReportingFor, setAdminReportingFor] = useState<string | null>(null)

  // Attestation state
  const [attestingReportId, setAttestingReportId] = useState<string | null>(null)

  // Komplettering state
  const [rejectingSubcontractor, setRejectingSubcontractor] = useState<string | null>(null)
  const [rejectFeedback, setRejectFeedback] = useState('')
  const [sendKompletteringSms, setSendKompletteringSms] = useState(true)
  const [manualCompletionFor, setManualCompletionFor] = useState<string | null>(null)
  const [manualCompletionReason, setManualCompletionReason] = useState('')
  const [processingReport, setProcessingReport] = useState(false)

  // Multi-SMS modal state
  const [showMultiSMSModal, setShowMultiSMSModal] = useState(false)

  // TG invoice registration state
  const [registeringInvoiceFor, setRegisteringInvoiceFor] = useState<{ reportId: string; name: string } | null>(null)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [savingInvoice, setSavingInvoice] = useState(false)

  // Removal confirmation state
  const [pendingRemoval, setPendingRemoval] = useState<{
    subcontractorId: string
    name: string
    status: TgWorkStatus
    hasReport: boolean
  } | null>(null)

  // Click outside handler for dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (subcontractorDropdownRef.current && !subcontractorDropdownRef.current.contains(event.target as Node)) {
        setShowSubcontractorSelector(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAttest = async (reportId: string, isCurrentlyAttested: boolean) => {
    setAttestingReportId(reportId)
    try {
      const now = new Date().toISOString()

      if (isCurrentlyAttested) {
        // Remove attestation
        await supabase
          .from('assignment_subcontractors')
          .update({
            attested_at: null,
            attested_by: null,
            tg_status: 'rapporterad' as TgWorkStatus
          })
          .eq('id', reportId)
        toast.success('Attestering borttagen')

        // If assignment was 'attesterad', revert to 'granskning' in DATABASE
        if (assignmentStatus === 'attesterad') {
          const { error: statusError } = await supabase
            .from('assignments')
            .update({
              status: 'granskning',
              updated_at: now
            })
            .eq('id', assignmentId)

          if (!statusError) {
            onStatusChange('granskning')
          }
        }
      } else {
        // Add attestation - need user_profiles.id (FK references user_profiles table)
        if (!profile?.user_id) {
          toast.error('Kunde inte attestera: Ingen inloggad användare')
          return
        }

        // Get user_profiles.id from user_id
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('user_id', profile.user_id)
          .single()

        const { error } = await supabase
          .from('assignment_subcontractors')
          .update({
            attested_at: now,
            attested_by: userProfile?.id || null,
            tg_status: 'godkand' as TgWorkStatus
          })
          .eq('id', reportId)

        if (error) {
          console.error('Attestation error:', error)
          toast.error(`Kunde inte attestera: ${error.message}`)
          return
        }
        toast.success('Rapport attesterad')

        // Lock prices on assignment articles (freeze current prices for statistics)
        try {
          await lockPricesForAssignment(assignmentId)
        } catch (lockErr) {
          console.error('Failed to lock prices:', lockErr)
        }

        // Check if ALL TGs are now approved (godkand)
        // Current report is being attested, so check others + this one
        const allOthersApproved = subcontractorReports
          .filter(r => r.id !== reportId)
          .every(r => r.tg_status === 'godkand' || r.tg_status === 'avsagt')

        // If all others are approved/declined, and we just approved this one,
        // update assignment status to 'attesterad' in DATABASE (not just React state)
        if (allOthersApproved && assignmentStatus === 'granskning') {
          const { error: statusError } = await supabase
            .from('assignments')
            .update({
              status: 'attesterad',
              updated_at: now
            })
            .eq('id', assignmentId)

          if (statusError) {
            console.error('Failed to update assignment status:', statusError)
          } else {
            onStatusChange('attesterad')
          }
        }
      }

      refetchReports()
      queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
    } catch (err) {
      console.error(err)
      toast.error('Kunde inte uppdatera attestering')
    } finally {
      setAttestingReportId(null)
    }
  }

  const handleRequestComplement = async (report: SubcontractorReport) => {
    if (!rejectFeedback.trim()) {
      toast.error('Ange feedback')
      return
    }

    setProcessingReport(true)
    try {
      const sub = report.subcontractors

      // Update the subcontractor record with feedback
      await supabase
        .from('assignment_subcontractors')
        .update({
          admin_feedback: `[BEGÄRD] ${rejectFeedback}`,
          feedback_at: new Date().toISOString(),
          reported_at: null,
          is_draft: true,
          tg_status: 'komplettering' as TgWorkStatus
        })
        .eq('id', report.id)

      // Update assignment status
      await supabase
        .from('assignments')
        .update({
          status: 'granskning',
          updated_at: new Date().toISOString()
        })
        .eq('id', assignmentId)

      // Log activity
      await logAssignmentChange(assignmentId, 'report_rejected', {
        description: `Begärde komplettering från ${sub?.name}: ${rejectFeedback}`
      })

      // Send SMS notification if checkbox is checked
      let smsSent = false
      if (sendKompletteringSms && sub?.phone) {
        const assignmentRef = assignmentNumber
          ? `#${assignmentNumber} - ${assignmentTitle}`
          : assignmentTitle || 'Uppdrag'
        const locationInfo = [assignmentAddress, assignmentDate].filter(Boolean).join(', ')
        const smsMessage = `Komplettering krävs för ${assignmentRef}. ${locationInfo ? locationInfo + '. ' : ''}${rejectFeedback}`

        const smsResult = await sendSMS({
          message: smsMessage,
          recipient_type: 'subcontractor',
          recipient_id: report.subcontractor_id,
          recipient_name: sub.name || 'Trädgårdsmästare',
          recipient_phone: sub.phone,
          assignment_id: assignmentId
        })

        if (!smsResult.success) {
          console.error('Failed to send SMS:', smsResult.error)
          toast.error('Kunde inte skicka SMS-notis')
        } else {
          smsSent = true
        }
      }

      toast.success('Komplettering begärd' + (smsSent ? ' och SMS skickat' : ''))
      setRejectingSubcontractor(null)
      setRejectFeedback('')
      onStatusChange('granskning')
      refetchReports()
      queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['activity-log', 'assignment', assignmentId] })
    } catch (err) {
      console.error(err)
      toast.error('Kunde inte spara')
    } finally {
      setProcessingReport(false)
    }
  }

  const handleManualCompletion = async (report: SubcontractorReport) => {
    if (!manualCompletionReason.trim()) {
      toast.error('Ange orsak till komplettering')
      return
    }

    setProcessingReport(true)
    try {
      const sub = report.subcontractors

      // Reset report to draft
      await supabase
        .from('assignment_subcontractors')
        .update({
          is_draft: true,
          reported_at: null,
          admin_feedback: `[MANUELL] ${manualCompletionReason}`,
          feedback_at: new Date().toISOString()
        })
        .eq('id', report.id)

      // Update assignment status
      await supabase
        .from('assignments')
        .update({
          status: 'granskning',
          updated_at: new Date().toISOString()
        })
        .eq('id', assignmentId)

      // Log the action
      await logAssignmentChange(assignmentId, 'report_reopened', {
        description: `Admin öppnade ${sub?.name}s rapport för manuell komplettering: ${manualCompletionReason}`
      })

      onStatusChange('granskning')
      setManualCompletionFor(null)
      setManualCompletionReason('')

      toast.success('Rapport öppnad för redigering')
      refetchReports()
      queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['activity-log', 'assignment', assignmentId] })
    } catch (err) {
      console.error(err)
      toast.error('Kunde inte öppna rapport')
    } finally {
      setProcessingReport(false)
    }
  }

  const handleRegisterInvoice = async () => {
    if (!registeringInvoiceFor) return

    if (!invoiceNumber.trim()) {
      toast.error('Ange fakturanummer')
      return
    }

    setSavingInvoice(true)
    try {
      const now = new Date().toISOString()

      const { error } = await supabase
        .from('assignment_subcontractors')
        .update({
          tg_invoice_status: 'faktura_mottagen',
          tg_invoice_number: invoiceNumber.trim(),
          tg_invoice_date: invoiceDate || null,
          tg_invoice_received_at: now,
          tg_personal_invoice_marked: true
        })
        .eq('id', registeringInvoiceFor.reportId)

      if (error) {
        console.error('Invoice registration error:', error)
        toast.error(`Kunde inte registrera faktura: ${error.message}`)
        return
      }

      await logAssignmentChange(assignmentId, 'tg_invoice_received', {
        description: `Registrerade faktura från ${registeringInvoiceFor.name}: ${invoiceNumber.trim()}`
      })

      // Check if assignment should auto-transition to slutförd
      // Requirements: status is attesterad + customer invoiced + all TG invoices now received
      if (assignmentStatus === 'attesterad' && customerInvoiceStatus === 'fakturerad') {
        const allTgInvoicesReceived = subcontractorReports.every(r => {
          if (r.id === registeringInvoiceFor.reportId) {
            return true // This one was just registered
          }
          return r.tg_invoice_status === 'faktura_mottagen' || r.tg_status === 'avsagt'
        })

        if (allTgInvoicesReceived) {
          const { error: statusError } = await supabase
            .from('assignments')
            .update({ status: 'slutford', updated_at: now })
            .eq('id', assignmentId)

          if (!statusError) {
            await logAssignmentChange(assignmentId, 'status_changed', {
              fieldName: 'status',
              oldValue: 'attesterad',
              newValue: 'slutford',
              description: 'Uppdraget slutfört automatiskt (kund fakturerad + alla TG-fakturor mottagna)'
            })
            onStatusChange('slutford')
            toast.success('Faktura registrerad! Uppdraget är nu slutfört.')
          } else {
            toast.success('Faktura registrerad')
          }
        } else {
          toast.success('Faktura registrerad')
        }
      } else {
        toast.success('Faktura registrerad')
      }

      setRegisteringInvoiceFor(null)
      setInvoiceNumber('')
      setInvoiceDate('')
      refetchReports()
      queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['activity-log', 'assignment', assignmentId] })
    } catch (err) {
      console.error(err)
      toast.error('Kunde inte registrera faktura')
    } finally {
      setSavingInvoice(false)
    }
  }

  // Handle removal request - check if confirmation is needed
  const handleRemoveRequest = (subcontractorId: string) => {
    // For new assignments, no confirmation needed
    if (isNewAssignment) {
      onRemoveSubcontractor(subcontractorId)
      return
    }

    // Find the report for this subcontractor
    const report = subcontractorReports.find(r => r.subcontractor_id === subcontractorId)

    if (!report) {
      // No report exists yet, just remove
      onRemoveSubcontractor(subcontractorId)
      return
    }

    const status = report.tg_status || 'preliminar_tilldelad'
    const hasReport = !!report.reported_at || !!report.report_text || (report.reported_articles && report.reported_articles.length > 0)

    // Statuses that don't need confirmation
    const noConfirmStatuses: TgWorkStatus[] = ['preliminar_tilldelad', 'avsagt']

    if (noConfirmStatuses.includes(status) && !hasReport) {
      onRemoveSubcontractor(subcontractorId)
      return
    }

    // Show confirmation for all other cases
    setPendingRemoval({
      subcontractorId,
      name: report.subcontractors.name,
      status,
      hasReport: hasReport || false
    })
  }

  const confirmRemoval = () => {
    if (pendingRemoval) {
      onRemoveSubcontractor(pendingRemoval.subcontractorId)
      setPendingRemoval(null)
    }
  }

  const getRemovalWarningMessage = () => {
    if (!pendingRemoval) return ''

    const { status, hasReport, name } = pendingRemoval

    if (hasReport) {
      return `${name} har rapporterat arbete för detta uppdrag. All rapportdata kommer att tas bort.`
    }

    switch (status) {
      case 'tilldelad':
      case 'ej_rapporterad':
        return `${name} är tilldelad och arbete kan ha påbörjats. Är du säker på att du vill ta bort?`
      case 'rapporterad':
        return `${name} har skickat in en rapport. All rapportdata kommer att tas bort.`
      case 'komplettering':
        return `${name} har en pågående komplettering. All data kommer att tas bort.`
      case 'godkand':
        return `${name} har en godkänd rapport. All rapportdata kommer att tas bort permanent.`
      default:
        return `Är du säker på att du vill ta bort ${name} från uppdraget?`
    }
  }

  const filteredSubcontractors = subcontractors
    .filter(s => !selectedSubcontractorIds.includes(s.id))
    .filter(s => s.name.toLowerCase().includes(subcontractorSearch.toLowerCase()))

  // Count for header - exclude declined TGs
  const activeCount = selectedSubcontractorIds.filter(id => {
    const report = subcontractorReports.find(r => r.subcontractor_id === id)
    return report?.tg_status !== 'avsagt'
  }).length
  const declinedCount = subcontractorReports.filter(r => r.tg_status === 'avsagt').length

  return (
    <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
      {/* Header */}
      <div className={`px-4 py-3 ${!isCollapsed ? 'border-b border-nature-200 dark:border-nature-700' : ''}`}>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Leaf className="h-5 w-5 text-nature-500" />
            <h2 className="text-base font-semibold text-nature-900 dark:text-nature-100">
              Trädgårdsmästare
            </h2>
            {activeCount > 0 && (
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full text-xs font-medium">
                {activeCount}
              </span>
            )}
            {declinedCount > 0 && (
              <span className="bg-nature-200 dark:bg-nature-600 text-nature-600 dark:text-nature-300 px-2 py-0.5 rounded-full text-xs font-medium">
                {declinedCount} avsagt
              </span>
            )}
          </button>

          <div className="flex items-center gap-2">
            {/* SMS till alla button */}
            {subcontractorReports.length > 1 && (
              <button
                onClick={() => setShowMultiSMSModal(true)}
                className="px-3 py-1.5 text-sm bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/30 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-lg flex items-center gap-1.5 transition-colors"
                title="Skicka SMS till alla trädgårdsmästare"
              >
                <MessageSquare className="h-4 w-4" />
                <span className="hidden sm:inline">SMS till alla</span>
              </button>
            )}

            {/* Add button with dropdown */}
            {!isLocked && (
              <div className="relative" ref={subcontractorDropdownRef}>
                <button
                  onClick={() => setShowSubcontractorSelector(!showSubcontractorSelector)}
                  className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Lägg till</span>
                </button>

                {showSubcontractorSelector && (
                  <div className="absolute right-0 z-50 mt-1 w-64 bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700">
                    <div className="p-2 border-b border-nature-200 dark:border-nature-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
                        <input
                          type="text"
                          value={subcontractorSearch}
                          onChange={(e) => setSubcontractorSearch(e.target.value)}
                          placeholder="Sök trädgårdsmästare..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredSubcontractors.map(sub => (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => {
                            onAddSubcontractor(sub.id)
                            setSubcontractorSearch('')
                            setShowSubcontractorSelector(false)
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700 flex items-center gap-2"
                        >
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: sub.color || '#6B7280' }}
                          />
                          <span className="text-sm text-nature-900 dark:text-nature-100">{sub.name}</span>
                        </button>
                      ))}
                      {filteredSubcontractors.length === 0 && (
                        <div className="px-4 py-3 text-sm text-nature-500 text-center">
                          {subcontractorSearch
                            ? 'Inga trädgårdsmästare hittades'
                            : 'Alla trädgårdsmästare är redan tilldelade'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Collapse toggle */}
            {onToggleCollapse && (
              <button
                type="button"
                onClick={onToggleCollapse}
                className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
              >
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
      <div className="p-4">
        {/* Empty state */}
        {activeCount === 0 && declinedCount === 0 && (
          <div className="text-center py-8 text-nature-500 dark:text-nature-400">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">Inga trädgårdsmästare tilldelade</p>
            <p className="text-sm mt-1">
              {isLocked
                ? 'Uppdraget är låst och kan inte ändras'
                : 'Klicka på "Lägg till" för att tilldela trädgårdsmästare'}
            </p>
          </div>
        )}

        {/* Show unsaved TGs (added but not yet saved) */}
        {!isNewAssignment && selectedSubcontractorIds.filter(id => !subcontractorReports.some(r => r.subcontractor_id === id)).length > 0 && (
          <div className="space-y-2 mb-4">
            {selectedSubcontractorIds
              .filter(id => !subcontractorReports.some(r => r.subcontractor_id === id))
              .map(subId => {
                const sub = subcontractors.find(s => s.id === subId)
                if (!sub) return null
                return (
                  <div
                    key={subId}
                    className="flex items-center justify-between p-3 bg-nature-50 dark:bg-nature-700/50 rounded-lg border-2 border-dashed border-nature-300 dark:border-nature-600"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: sub.color || '#6B7280' }}
                      />
                      <span className="font-medium text-nature-900 dark:text-nature-100">
                        {sub.name}
                      </span>
                    </div>
                    {!isLocked && (
                      <button
                        onClick={() => handleRemoveRequest(subId)}
                        className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                      >
                        Ta bort
                      </button>
                    )}
                  </div>
                )
              })}
          </div>
        )}

        {/* For new assignments - show simple list of selected TGs */}
        {isNewAssignment && selectedSubcontractorIds.length > 0 && (
          <div className="space-y-2">
            {selectedSubcontractorIds.map(subId => {
              const sub = subcontractors.find(s => s.id === subId)
              if (!sub) return null
              return (
                <div
                  key={subId}
                  className="flex items-center justify-between p-3 bg-nature-50 dark:bg-nature-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: sub.color || '#6B7280' }}
                    />
                    <span className="font-medium text-nature-900 dark:text-nature-100">
                      {sub.name}
                    </span>
                  </div>
                  {!isLocked && (
                    <button
                      onClick={() => handleRemoveRequest(subId)}
                      className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      Ta bort
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* For existing assignments - show full SubcontractorStatusCard for each TG */}
        {!isNewAssignment && subcontractorReports.length > 0 && (
          <div className="space-y-4">
            {/* Active TGs */}
            {subcontractorReports
              .filter(report => selectedSubcontractorIds.includes(report.subcontractor_id) && report.tg_status !== 'avsagt')
              .map((report) => {
              const sub = report.subcontractors
              const hasReported = !!report.reported_at && !report.is_draft

              return (
                <div key={report.id} className="space-y-2">
                  <SubcontractorStatusCard
                    subcontractor={{
                      id: sub.id,
                      name: sub.name,
                      email: sub.email,
                      phone: sub.phone,
                      color: sub.color
                    }}
                    assignmentId={assignmentId}
                    workStatus={report.tg_status || 'preliminar_tilldelad'}
                    invoiceStatus={report.tg_invoice_status || 'ej_fakturerad'}
                    report={report.reported_at || report.is_draft ? {
                      id: report.id,
                      report_text: report.report_text,
                      reported_at: report.reported_at,
                      admin_feedback: report.admin_feedback,
                      feedback_at: report.feedback_at,
                      reported_articles: report.reported_articles?.map(a => ({
                        article_id: a.article_id,
                        article_name: a.article_name,
                        unit: a.unit || 'st',
                        planned_quantity: a.planned_quantity || 0,
                        reported_quantity: a.actual_quantity || 0,
                        notes: a.notes,
                        images: a.images,
                        is_expense: a.is_expense,
                        expense_amount_incl_vat: a.expense_amount_incl_vat,
                        expense_amount_excl_vat: a.expense_amount_excl_vat,
                        expense_vat_rate: a.expense_vat_rate
                      })),
                      report_images: report.report_images?.map(img => ({
                        url: img.url,
                        type: img.type as 'before' | 'after' | 'other',
                        caption: img.name
                      })),
                      is_draft: report.is_draft,
                      attested_at: report.attested_at,
                      attested_by: (report as any).attester?.full_name || null,
                      draft_saved_at: report.draft_saved_at
                    } : null}
                    declinedAt={report.declined_at}
                    declinedReason={report.declined_reason}
                    invoiceReceivedAt={report.tg_invoice_received_at}
                    invoiceNumber={report.tg_invoice_number}
                    isReportingFor={adminReportingFor === report.subcontractor_id}
                    reportFormContent={
                      adminReportingFor === report.subcontractor_id ? (
                        <AdminReportForm
                          assignmentId={assignmentId}
                          reportId={report.id}
                          subcontractorId={report.subcontractor_id}
                          subcontractorName={sub.name}
                          plannedArticles={plannedArticles.map(pa => ({
                            articleId: pa.articleId,
                            quantity: pa.subcontractorQuantities?.[report.subcontractor_id] ?? pa.quantity
                          }))}
                          articles={articles}
                          existingDraft={report.is_draft ? {
                            reportText: report.report_text || null,
                            reportedArticles: report.reported_articles || null,
                            reportImages: report.report_images || null,
                            isDraft: true
                          } : null}
                          adminFeedback={report.admin_feedback}
                          currentAssignmentStatus={assignmentStatus}
                          onComplete={() => {
                            setAdminReportingFor(null)
                            refetchReports()
                            queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
                          }}
                          onCancel={() => setAdminReportingFor(null)}
                        />
                      ) : undefined
                    }
                    onStartReporting={() => setAdminReportingFor(report.subcontractor_id)}
                    onCancelReporting={() => setAdminReportingFor(null)}
                    onRemove={() => handleRemoveRequest(report.subcontractor_id)}
                    onNavigateToSubcontractor={() => navigate(`/admin/subcontractors/${report.subcontractor_id}`)}
                    onAttest={hasReported ? () => handleAttest(report.id, false) : undefined}
                    onRemoveAttest={report.attested_at ? () => handleAttest(report.id, true) : undefined}
                    onRequestComplement={hasReported && !report.attested_at ? () => setRejectingSubcontractor(report.subcontractor_id) : undefined}
                    onReportFor={hasReported && !report.attested_at ? () => setManualCompletionFor(report.subcontractor_id) : undefined}
                    onRegisterInvoice={report.tg_status === 'godkand' && report.tg_invoice_status !== 'faktura_mottagen' ? () => setRegisteringInvoiceFor({ reportId: report.id, name: sub.name }) : undefined}
                    disabled={attestingReportId === report.id}
                    isLocked={isLocked}
                    showReportSection={true}
                    showSMSSection={true}
                  />

                  {/* Reject feedback form (komplettering) */}
                  {rejectingSubcontractor === report.subcontractor_id && (
                    <div className="ml-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                      <label className="block text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
                        Ange vad som behöver kompletteras:
                      </label>
                      <textarea
                        value={rejectFeedback}
                        onChange={(e) => setRejectFeedback(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-orange-300 dark:border-orange-700 rounded bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100"
                        rows={3}
                        placeholder="Beskriv vad som saknas eller behöver ändras..."
                      />

                      {/* SMS checkbox and preview */}
                      {report.subcontractors?.phone && (
                        <div className="mt-3 p-2 bg-white dark:bg-nature-800 border border-orange-200 dark:border-orange-700 rounded">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sendKompletteringSms}
                              onChange={(e) => setSendKompletteringSms(e.target.checked)}
                              className="h-4 w-4 text-orange-600 rounded"
                            />
                            <MessageSquare className="h-4 w-4 text-orange-600" />
                            <span className="text-sm text-nature-700 dark:text-nature-300">
                              Skicka SMS till {report.subcontractors.name}
                            </span>
                          </label>

                          {sendKompletteringSms && rejectFeedback.trim() && (
                            <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/30 rounded text-xs text-orange-700 dark:text-orange-300 italic">
                              <p className="font-medium mb-1">SMS-förhandsgranskning:</p>
                              <p>Komplettering krävs för {assignmentNumber ? `#${assignmentNumber} - ${assignmentTitle}` : assignmentTitle}. {[assignmentAddress, assignmentDate].filter(Boolean).join(', ')}{(assignmentAddress || assignmentDate) ? '. ' : ''}{rejectFeedback}</p>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleRequestComplement(report)}
                          disabled={processingReport || !rejectFeedback.trim()}
                          className="px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded flex items-center gap-1 disabled:opacity-50"
                        >
                          {processingReport && <Loader2 className="h-3 w-3 animate-spin" />}
                          Skicka
                        </button>
                        <button
                          onClick={() => {
                            setRejectingSubcontractor(null)
                            setRejectFeedback('')
                            setSendKompletteringSms(true)
                          }}
                          className="px-3 py-1.5 text-sm bg-nature-200 dark:bg-nature-600 text-nature-700 dark:text-nature-200 rounded"
                        >
                          Avbryt
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Manual completion form */}
                  {manualCompletionFor === report.subcontractor_id && (
                    <div className="ml-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <label className="block text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        Orsak till manuell komplettering:
                      </label>
                      <textarea
                        value={manualCompletionReason}
                        onChange={(e) => setManualCompletionReason(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100"
                        rows={3}
                        placeholder="Beskriv varför rapporten behöver kompletteras manuellt..."
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleManualCompletion(report)}
                          disabled={processingReport}
                          className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1"
                        >
                          {processingReport && <Loader2 className="h-3 w-3 animate-spin" />}
                          Öppna för redigering
                        </button>
                        <button
                          onClick={() => {
                            setManualCompletionFor(null)
                            setManualCompletionReason('')
                          }}
                          className="px-3 py-1.5 text-sm bg-nature-200 dark:bg-nature-600 text-nature-700 dark:text-nature-200 rounded"
                        >
                          Avbryt
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}


            {/* Declined TGs section */}
            {subcontractorReports.filter(r => r.tg_status === 'avsagt').length > 0 && (
              <div className="pt-4 border-t border-nature-200 dark:border-nature-700">
                <h4 className="text-sm font-medium text-nature-500 dark:text-nature-400 mb-3 flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Har avsagt sig ({subcontractorReports.filter(r => r.tg_status === 'avsagt').length})
                </h4>
                <div className="space-y-2">
                  {subcontractorReports
                    .filter(report => report.tg_status === 'avsagt')
                    .map((report) => {
                      const sub = report.subcontractors
                      return (
                        <SubcontractorStatusCard
                          key={report.id}
                          subcontractor={{
                            id: sub.id,
                            name: sub.name,
                            email: sub.email,
                            phone: sub.phone,
                            color: sub.color
                          }}
                          assignmentId={assignmentId}
                          workStatus="avsagt"
                          invoiceStatus={report.tg_invoice_status || 'ej_fakturerad'}
                          declinedAt={report.declined_at}
                          declinedReason={report.declined_reason}
                          onRemove={() => handleRemoveRequest(report.subcontractor_id)}
                          onNavigateToSubcontractor={() => navigate(`/admin/subcontractors/${report.subcontractor_id}`)}
                          isLocked={false}
                          showReportSection={false}
                          showSMSSection={false}
                          defaultExpanded={false}
                        />
                      )
                    })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Removal Confirmation Modal */}
      {pendingRemoval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Ta bort trädgårdsmästare?
              </h3>
              <button
                onClick={() => setPendingRemoval(null)}
                className="ml-auto p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-nature-700 dark:text-nature-300">
                {getRemovalWarningMessage()}
              </p>
              {pendingRemoval.hasReport && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                    Varning: Detta kan inte ångras!
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 flex justify-end gap-3">
              <button
                onClick={() => setPendingRemoval(null)}
                className="px-4 py-2 text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={confirmRemoval}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Ta bort
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-SMS Modal */}
      <SendMultiSMSModal
        isOpen={showMultiSMSModal}
        onClose={() => setShowMultiSMSModal(false)}
        recipients={subcontractorReports.map(r => ({
          id: r.subcontractor_id,
          name: r.subcontractors.name,
          phone: r.subcontractors.phone || ''
        }))}
        assignmentId={assignmentId}
        onMessagesSent={() => {
          queryClient.invalidateQueries({ queryKey: ['sms-messages', assignmentId] })
        }}
      />

      {/* TG Invoice Registration Modal */}
      {registeringInvoiceFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Registrera faktura
              </h3>
              <p className="text-sm text-nature-500 dark:text-nature-400 mt-1">
                Från {registeringInvoiceFor.name}
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Fakturanummer *
                </label>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
                  placeholder="T.ex. F2025-001"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Fakturadatum
                </label>
                <input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setRegisteringInvoiceFor(null)
                  setInvoiceNumber('')
                  setInvoiceDate('')
                }}
                className="px-4 py-2 text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleRegisterInvoice}
                disabled={savingInvoice || !invoiceNumber.trim()}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {savingInvoice && <Loader2 className="h-4 w-4 animate-spin" />}
                Registrera
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
